import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DropdownOption } from "@cms/shared-library";
import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from "ng-recaptcha-2";
import { Subscription, debounceTime, forkJoin, map, startWith } from 'rxjs';
import { ApprovedTrainingProviderSearchResultModel } from 'src/app/models/approved-training-provider-search-result.model';
import { TrainingProviderSearchForm } from 'src/app/models/training-provider-search-form.model';
import { TrainingProvider } from 'src/app/models/training-provider.model';
import { TrainingType } from 'src/app/models/training-type.model';
import { CourseService } from 'src/app/services/course/course.service';
import { TrainingProgramService } from 'src/app/services/training-program/training-program.service';
import { TrainingProviderService } from 'src/app/services/training-provider/training-provider.service';
import { TrainingTypeService } from 'src/app/services/training-type/training-type.service';
import { formatAddress } from 'src/app/utils/address.util';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';

@Component({
  selector: 'app-training-providers-search',
  templateUrl: './training-providers-search.component.html',
  styleUrls: ['./training-providers-search.component.scss'],
  standalone: false
})
export class TrainingProvidersSearchComponent implements OnInit {
  //captcha
  public recentToken = "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public recentError?: { error: any };
  private singleExecutionSubscription: Subscription;

  TrainingProviderDropdown: TrainingProvider[] = [];
  selectedTrainingProvider = this.TrainingProviderDropdown;
  trainingProgramOptions: DropdownOption[] = [new DropdownOption('All Safety Training Programs', 'Tous les programmes de formation en sécurité', '00000000-0000-0000-0000-000000000000')];
  cpoApprovalStatusOptions: DropdownOption[] = [new DropdownOption('All training providers', 'Tous les fournisseurs de services de formation agréés', ''), new DropdownOption('Currently CPO-approved', 'Actuellement approuvé par le DGP', 'true'), new DropdownOption('No longer approved', 'N’est plus approuvé', 'false')];
  trainingTypes: TrainingType[] = [new TrainingType('All', 'All', 'Tous')];
  trainingTypeIdSelected: string[] = [];
  trainingProviderSearchForm = new TrainingProviderSearchForm();
  currentLang: string = 'en'
  trainingProviderSearchResults: ApprovedTrainingProviderSearchResultModel[] = [];
  filteredProviderOptions: TrainingProvider[];
  trainingProviderControl = new FormControl();
  tpAppUtils = TrainingProgramApplicationUtils

  constructor(public translateService: TranslateService,
    @Inject('CourseService') public courseService: CourseService,
    @Inject('TrainingProviderService') public trainingProviderService: TrainingProviderService,
    @Inject('TrainingTypeService') public trainingTypeService: TrainingTypeService,
    @Inject('TrainingProgramService') public trainingProgramService: TrainingProgramService,
    private recaptchaV3Service: ReCaptchaV3Service) {
    this.currentLang = translateService.currentLang;
    translateService.onLangChange.subscribe(lang => this.currentLang = lang.lang);
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    forkJoin({
      trainingTypesData: this.trainingTypeService.GetAllTrainingTypes(this.currentLang == 'fr'),
      trainingProgramData: this.trainingProgramService.GetAllTrainingPrograms(this.currentLang == 'fr')
    }).subscribe(data => {
      this.trainingTypes = this.trainingTypes.concat(data.trainingTypesData);
      this.addToSelectedTypeId('All');
      this.trainingProviderControl.setValue('');
      this.trainingProgramOptions = this.trainingProgramOptions.concat(data.trainingProgramData.map(x => { return new DropdownOption(x.Name, x.NameFr, x.TrainingProgramId) }))
      this.searchTrainingProviders()

      this.trainingProviderControl.valueChanges.pipe(
        startWith(''),
        map(value => (typeof value === 'string' ? value : value?.name)),
        debounceTime(200)
      ).subscribe(name => {
        this.filteredProviderOptions = []
        if (name != null && name.length >= 1)
          this.trainingProviderService.GetAllTrainingProvidersBySearchFragment(name).subscribe(data => this.filteredProviderOptions = data);
      });

    })
  }

  public ngOnDestroy(): void { if (this.singleExecutionSubscription) this.singleExecutionSubscription.unsubscribe(); }

  addToSelectedTypeId(id: string) {
    if (id == 'All')
      this.trainingTypeIdSelected = this.trainingTypes.map(x => x.TrainingTypeId);
    else if (this.trainingTypeIdSelected.indexOf(id) > -1)
      this.trainingTypeIdSelected = this.trainingTypeIdSelected.filter(x => x != id && x != 'All')
    else {
      this.trainingTypeIdSelected.push(id)
      if (this.trainingTypeIdSelected.length + 1 == this.trainingTypes.length)
        this.trainingTypeIdSelected.push('All')
    }
  }

  searchTrainingProviders(keepData: boolean = false) {
    if (this.singleExecutionSubscription) {
      this.singleExecutionSubscription.unsubscribe();
    }
    this.singleExecutionSubscription = this.recaptchaV3Service.execute('TproviderSearch').subscribe(
      (token) => {
        this.recentToken = token;
        this.recentError = undefined;
        if (!keepData) {
          this.trainingProviderSearchForm.pageIndex = 0
          this.trainingProviderSearchResults = []
        }
        this.trainingProviderSearchForm.captchaToken = token;
        this.trainingProviderSearchForm.trainingTypeIdCommaSeparated = this.trainingTypeIdSelected.join(',');
        this.trainingProviderSearchForm.trainingProviderName = this.trainingProviderControl.value != null ? this.trainingProviderControl.value.ApplicantBusinessOperatingName : null
        this.trainingProviderService.GetTrainingProviderBySearchParams(this.trainingProviderSearchForm).subscribe(response => {
          response.forEach(x => {
            x.Expanded = false
            x.FormattedAddress = formatAddress(x, true, true)
          })
          this.trainingProviderSearchResults.push(...response);
        });
      },
      (error) => {
        this.recentToken = "";
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.recentError = { error };
        console.log(error);
      },
    );
  }

  getCourseApprovalData(tp: ApprovedTrainingProviderSearchResultModel, isExpand: boolean) {
    if (isExpand && tp.HasSearchedForApprovals != true && tp.TrainingProviderId != undefined) {
      this.trainingProviderService.GetTrainingProviderCourseApprovals(tp.TrainingProviderId).subscribe(data => {
        tp.CourseApprovals = data
        tp.HasSearchedForApprovals = true

        tp.TabOptions = []
        if (tp.HasWAHApproval) tp.TabOptions.push(new DropdownOption('WAH', 'Travail en hauteur', 'wah'))
        if (tp.HasJHSCApproval) tp.TabOptions.push(new DropdownOption('JHSC', 'CMSST', 'jhsc'))

        if (tp.TabOptions.length > 0) tp.SelectedOption = tp.TabOptions[0].value
        if (tp.SelectedOption == undefined) tp.SelectedOption = ''
        this.filterTPCourseApprovals(tp, tp.SelectedOption)
      })
    }
  }

  filterTPCourseApprovals(tp: ApprovedTrainingProviderSearchResultModel, programType: any) {
    tp.FilteredCourseApprovals = tp.CourseApprovals?.filter(x => x.TrainingProgramName.toLowerCase().indexOf(programType) > -1 && x.ApprovedOnDate)
  }

  displayFn(trainingProvider: TrainingProvider): string {
    return trainingProvider && trainingProvider.ApplicantBusinessOperatingName ? trainingProvider.ApplicantBusinessOperatingName : '';
  }

  initSearchForm() {
    this.trainingProviderSearchForm = new TrainingProviderSearchForm();
    this.trainingTypeIdSelected = [];
    this.trainingProviderSearchResults = []
  }

}


